import { useState, type ReactNode } from 'react'
import { Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { toast } from 'sonner'
import { getErrorMessage } from '@/api/errors'
import { draftResolution, GITHUB_ISSUE, RELATION_CHOICES, SEVERITY_HINT, SEVERITY_LABEL, STATE_LABEL, useConversation, useDeskAction, useDeskCases, useDeskMeta, useLogTicket, type IntercomDetail, type RelationKind, type ResolutionSummary, type Severity, type SupportCase } from './api'
import { useClients, useNow } from './parts'

/**
 * One small dialog per action. Each asks only for what the action needs (at most two required
 * inputs); everything else is filled in by the backend.
 */

const SEVERITIES: Severity[] = ['P1', 'P2', 'P3', 'P4', 'REQ']
const HELPDESK = '__helpdesk__'
const NONE = '__none__'

function Shell({ open, onOpenChange, title, description, children, submit, busy, disabled }: {
  open: boolean
  onOpenChange: (v: boolean) => void
  title: string
  description?: string
  children: ReactNode
  submit: { label: string; onClick: () => void }
  busy: boolean
  disabled?: boolean
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        <div className="grid gap-3">{children}</div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit.onClick} disabled={busy || disabled}>{busy ? 'Saving…' : submit.label}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SeverityPicker({ value, onChange }: { value: Severity | ''; onChange: (s: Severity) => void }) {
  return (
    <div className="grid gap-1.5">
      <Label>Priority</Label>
      <Select value={value} onValueChange={(v) => onChange(v as Severity)}>
        <SelectTrigger><SelectValue placeholder="Pick a priority" /></SelectTrigger>
        <SelectContent>
          {SEVERITIES.map((s) => (
            <SelectItem key={s} value={s}>
              {SEVERITY_LABEL[s]} — <span className="text-muted-foreground">{SEVERITY_HINT[s]}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

interface HandoffDraft {
  impact: string
  expectedActual: string
  steps: string
  occurredAt: string
  evidence: string
  workaround: string
}
const handoffReady = (h: HandoffDraft) => Boolean(h.impact.trim() && h.expectedActual.trim() && h.steps.trim())
const handoffBody = (h: HandoffDraft) => ({
  steps: h.steps.trim(),
  handoff: { impact: h.impact.trim(), expectedActual: h.expectedActual.trim(), occurredAt: h.occurredAt.trim() || null, evidence: h.evidence.trim() || null, workaround: h.workaround.trim() || null },
})

/** The engineering handoff: everything a team needs to start without coming back to helpdesk. */
function HandoffFields({ value, onChange }: { value: HandoffDraft; onChange: (v: HandoffDraft) => void }) {
  const field = (key: keyof HandoffDraft, label: string, placeholder: string, rows = 2) => (
    <div className="grid gap-1.5">
      <Label htmlFor={`sd-h-${key}`}>{label}</Label>
      <Textarea id={`sd-h-${key}`} rows={rows} value={value[key]} onChange={(e) => onChange({ ...value, [key]: e.target.value })} placeholder={placeholder} />
    </div>
  )
  return (
    <div className="grid gap-3 rounded-md border border-border-soft p-3">
      <p className="text-xs font-semibold">Engineering handoff</p>
      {field('impact', 'Who is affected', 'Client, users or accounts, how many, how badly')}
      {field('expectedActual', 'Expected vs actual', 'What should happen, and what happens instead')}
      {field('steps', 'Steps to reproduce', 'What the customer did, step by step. Or: not reproducible.', 3)}
      <div className="grid gap-1.5">
        <Label htmlFor="sd-h-occurredAt">When it happened (optional)</Label>
        <Input id="sd-h-occurredAt" value={value.occurredAt} onChange={(e) => onChange({ ...value, occurredAt: e.target.value })} placeholder="e.g. 21 Oct, 14:05 Manila time" />
      </div>
      {field('evidence', 'Evidence (optional)', 'Links to screenshots, logs or recordings')}
      {field('workaround', 'Tried so far / workaround (optional)', 'What helpdesk already tried, and any workaround given')}
    </div>
  )
}

const manila = (iso: string) =>
  `${new Date(iso).toLocaleString('en-PH', { timeZone: 'Asia/Manila', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })} Manila time`
const isCustomer = (type: string | null) => type === 'user' || type === 'contact' || type === 'lead'

/**
 * The handoff, filled from what the customer already told us: who they are, what they reported,
 * their own messages, when they first wrote, and any files they sent. Helpdesk checks and edits it.
 */
function prefillHandoff(c: SupportCase, detail: IntercomDetail | undefined): HandoffDraft {
  const report = (detail?.description?.trim() || c.summary?.trim() || c.title).slice(0, 1_500)
  const customerParts = (detail?.parts ?? []).filter((p) => isCustomer(p.author.type))
  const messages = customerParts
    .map((p) => p.body?.trim())
    .filter((b): b is string => Boolean(b) && b !== report)
    .slice(0, 4)
    .join('\n')
    .slice(0, 1_500)
  const files = [...new Set(customerParts.flatMap((p) => (p.attachments ?? []).map((a) => `${a.name}: ${a.url}`)))].slice(0, 10)
  return {
    impact: [c.clientName ?? 'Client not matched', c.requester ? `reported by ${c.requester}` : null].filter(Boolean).join(' — '),
    expectedActual: report,
    steps: messages ? `From the customer's messages (confirm or rewrite):\n${messages}` : '',
    occurredAt: manila(c.firstContactAt),
    evidence: files.join('\n'),
    workaround: '',
  }
}

/** Triage and send: priority + owner in one step. Pre-filled from the AI suggestion. */

export function TriageDialog({ c, open, onOpenChange }: { c: SupportCase; open: boolean; onOpenChange: (v: boolean) => void }) {
  const meta = useDeskMeta()
  const run = useDeskAction()
  const [severity, setSeverity] = useState<Severity | ''>(c.severity ?? c.suggestedSeverity ?? '')
  const suggestedOwner = c.suggestedTeam && c.suggestedTeam !== 'Helpdesk' ? c.suggestedTeam : HELPDESK
  const [owner, setOwner] = useState<string>(c.ownerTeam ?? suggestedOwner)
  // The team lead is assigned unless someone else is picked; changing the team resets it to that team's lead.
  const [picked, setPicked] = useState<string | null>(null)
  // The handoff starts filled from what the customer already told us; edits replace it.
  const conv = useConversation(c.sourceId, c.sourceKind !== 'manual')
  const [edited, setEdited] = useState<HandoffDraft | null>(null)
  const handoff = edited ?? prefillHandoff(c, conv.data)
  const setHandoff = setEdited
  const team = owner === HELPDESK ? null : owner
  const people = meta.data?.teams.find((t) => t.name === team)?.members ?? []
  const assignee = picked ?? people.find((p) => p.isLead)?.id ?? NONE
  const setAssignee = (v: string) => setPicked(v)
  // A ticket sent to a team always has a named person on it, so nobody has to wonder who owns it.
  const ready = Boolean(severity) && (!team || (handoffReady(handoff) && assignee !== NONE))
  return (
    <Shell
      open={open}
      onOpenChange={onOpenChange}
      title="Triage"
      description="Confirm the priority and who owns it. The clock has been running since the customer's first message."
      busy={run.isPending}
      disabled={!ready}
      submit={{
        label: team ? `Send to ${team}` : 'Keep with helpdesk',
        onClick: () =>
          run.mutate(
            { id: c.id, action: 'triage', body: { severity, team, assigneeId: assignee === NONE ? null : assignee, ...(team ? handoffBody(handoff) : {}) } },
            { onSuccess: () => onOpenChange(false) },
          ),
      }}
    >
      {c.suggestionReason && c.suggestedSeverity ? (
        <p className="flex items-start gap-2 rounded-md bg-brand-softer px-3 py-2 text-xs">
          <Sparkles className="mt-0.5 size-3.5 shrink-0 text-brand" />
          <span>Suggested: <strong>{SEVERITY_LABEL[c.suggestedSeverity]}</strong>{c.suggestedTeam ? <> · <strong>{c.suggestedTeam}</strong></> : null}. {c.suggestionReason}</span>
        </p>
      ) : null}
      <SeverityPicker value={severity} onChange={setSeverity} />
      <div className="grid gap-1.5">
        <Label>Owner</Label>
        <Select value={owner} onValueChange={(v) => { setOwner(v); setPicked(null) }}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={HELPDESK}>Helpdesk resolves it</SelectItem>
            {(meta.data?.teams ?? []).map((t) => <SelectItem key={t.name} value={t.name}>Send to {t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {team ? (
        <>
          <div className="grid gap-1.5">
            <Label>Assign to</Label>
            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE} disabled>Pick who works on it</SelectItem>
                {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.displayName}{p.isLead ? ' (lead)' : ''}</SelectItem>)}
              </SelectContent>
            </Select>
            {!people.length ? <p className="text-tiny text-danger">Nobody is set up on this team yet. Add its people under Support teams first.</p> : null}
          </div>
          <HandoffFields value={handoff} onChange={setHandoff} />
        </>
      ) : null}
    </Shell>
  )
}

export function SeverityDialog({ c, open, onOpenChange }: { c: SupportCase; open: boolean; onOpenChange: (v: boolean) => void }) {
  const run = useDeskAction()
  const [severity, setSeverity] = useState<Severity | ''>(c.severity ?? '')
  const [reason, setReason] = useState('')
  return (
    <Shell
      open={open}
      onOpenChange={onOpenChange}
      title="Change priority"
      description="The clock keeps running. Targets are recalculated for the new priority from the customer's first message."
      busy={run.isPending}
      disabled={!severity || severity === c.severity || !reason.trim()}
      submit={{ label: 'Change priority', onClick: () => run.mutate({ id: c.id, action: 'severity', body: { severity, reason } }, { onSuccess: () => onOpenChange(false) }) }}
    >
      <SeverityPicker value={severity} onChange={setSeverity} />
      <div className="grid gap-1.5">
        <Label htmlFor="sd-sev-reason">Why</Label>
        <Input id="sd-sev-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. More clients affected than first reported" />
      </div>
    </Shell>
  )
}

export function TextActionDialog({ c, open, onOpenChange, action, title, description, label, field, placeholder, submitLabel }: {
  c: SupportCase
  open: boolean
  onOpenChange: (v: boolean) => void
  action: 'ask' | 'restored' | 'reopen' | 'close' | 'update' | 'close-no-response' | 'blocked'
  title: string
  description: string
  label: string
  field: 'question' | 'note' | 'reason' | 'text'
  placeholder: string
  submitLabel: string
}) {
  const run = useDeskAction()
  const [text, setText] = useState('')
  const optional = action === 'close' || action === 'update'
  return (
    <Shell
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      busy={run.isPending}
      disabled={!optional && !text.trim()}
      submit={{ label: submitLabel, onClick: () => run.mutate({ id: c.id, action, body: text.trim() ? { [field]: text.trim() } : {} }, { onSuccess: () => { setText(''); onOpenChange(false) } }) }}
    >
      <div className="grid gap-1.5">
        <Label htmlFor="sd-text">{label}</Label>
        <Textarea id="sd-text" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} />
      </div>
    </Shell>
  )
}

export const CLIENT_SIDE_CAUSE = 'Client-side (their environment)'

/**
 * Engineering found the cause is in the client's own environment. Their part ends: restore and fix count as
 * met now, and the ticket goes back to helpdesk to follow up with the client and close.
 */
/**
 * The customer came back while a fix was being confirmed. Same problem: reopen it to whoever had it,
 * with the clock picking up where it stopped. Something new: the fix stood, so the ticket closes and
 * the new request opens as its own ticket, linked, which then opens here.
 */
export function NotResolvedDialog({ c, open, onOpenChange, onOpenCase }: { c: SupportCase; open: boolean; onOpenChange: (v: boolean) => void; onOpenCase: (id: string) => void }) {
  const run = useDeskAction()
  const [kind, setKind] = useState<'same' | 'new'>('same')
  const [note, setNote] = useState('')
  const [title, setTitle] = useState('')
  const owner = c.ownerTeam ?? 'helpdesk'
  const choices = [
    { id: 'same' as const, label: 'Still broken', hint: `The same problem is back or was never fixed. It reopens to ${owner}; the clock picks up where it stopped when it was marked fixed.` },
    { id: 'new' as const, label: 'Needs something new', hint: 'The fix worked, but they need help with something else. This ticket closes and the new request opens as its own ticket, linked to it.' },
  ]
  return (
    <Shell
      open={open}
      onOpenChange={onOpenChange}
      title="Not resolved"
      description="The customer came back while we were waiting for them to confirm the fix."
      busy={run.isPending}
      disabled={!note.trim() || (kind === 'new' && !title.trim())}
      submit={{
        label: kind === 'same' ? 'Reopen ticket' : 'Close and open the new request',
        onClick: () =>
          run.mutate(
            { id: c.id, action: 'not-resolved', body: kind === 'same' ? { kind, note } : { kind, title, note } },
            {
              onSuccess: (next) => {
                onOpenChange(false)
                if (next && next.id !== c.id) onOpenCase(next.id)
              },
            },
          ),
      }}
    >
      <div className="grid gap-2" role="radiogroup" aria-label="What the customer needs">
        {choices.map((o) => (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={kind === o.id}
            onClick={() => setKind(o.id)}
            className={`rounded-lg border p-3 text-left ${kind === o.id ? 'border-primary bg-accent' : 'border-border hover:bg-muted'}`}
          >
            <span className="block text-sm font-semibold">{o.label}</span>
            <span className="block text-xs text-text-secondary">{o.hint}</span>
          </button>
        ))}
      </div>
      {kind === 'new' ? (
        <div className="grid gap-1.5">
          <Label htmlFor="sd-nr-title">The new request</Label>
          <Input id="sd-nr-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Add a second admin user for the PH team" />
        </div>
      ) : null}
      <div className="grid gap-1.5">
        <Label htmlFor="sd-nr-note">What the customer said</Label>
        <Textarea id="sd-nr-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={kind === 'same' ? 'e.g. Still no reset email after following the steps; tried twice this afternoon' : 'e.g. Password works now; they also need another admin added'} />
      </div>
    </Shell>
  )
}

type Outcome = 'fixed' | 'client_side' | 'not_needed'
const OUTCOMES: Array<{ id: Outcome; label: string; hint: string }> = [
  { id: 'fixed', label: 'Fixed', hint: 'We fixed it before it was closed.' },
  { id: 'client_side', label: 'Client-side cause', hint: "The cause was in the client's own setup." },
  { id: 'not_needed', label: 'Not needed', hint: 'No fix was needed (withdrawn, duplicate, works as designed).' },
]

/** How engineering's part ended, for a ticket helpdesk closed in Intercom while engineering had it. */
export function OutcomeDialog({ c, open, onOpenChange }: { c: SupportCase; open: boolean; onOpenChange: (v: boolean) => void }) {
  const meta = useDeskMeta()
  const run = useDeskAction()
  const [outcome, setOutcome] = useState<Outcome>('fixed')
  const [note, setNote] = useState('')
  const [cause, setCause] = useState('')
  const [github, setGithub] = useState(c.githubUrl ?? '')
  const [evidence, setEvidence] = useState('')
  const [finding, setFinding] = useState('')
  const [clientAction, setClientAction] = useState('')
  const issueOk = !github.trim() || GITHUB_ISSUE.test(github.trim())
  const ready =
    outcome === 'fixed' ? Boolean(note.trim() && cause && issueOk) : outcome === 'client_side' ? Boolean(finding.trim() && clientAction.trim()) : Boolean(note.trim())
  const body =
    outcome === 'fixed'
      ? { outcome, note, rootCause: cause, githubUrl: github.trim() || null, evidence: evidence.trim() || null }
      : outcome === 'client_side'
        ? { outcome, finding, clientAction }
        : { outcome, note }
  return (
    <Shell
      open={open}
      onOpenChange={onOpenChange}
      title="Record the outcome"
      description="Helpdesk closed the customer conversation in Intercom while engineering still had this ticket. Say how it ended. Fixed and client-side count as done when it was closed; Not needed counts nothing. Nothing is sent to the customer."
      busy={run.isPending}
      disabled={!ready}
      submit={{ label: 'Record outcome', onClick: () => run.mutate({ id: c.id, action: 'outcome', body }, { onSuccess: () => onOpenChange(false) }) }}
    >
      <div className="grid gap-2" role="radiogroup" aria-label="Outcome">
        {OUTCOMES.map((o) => (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={outcome === o.id}
            onClick={() => setOutcome(o.id)}
            className={`rounded-lg border p-3 text-left ${outcome === o.id ? 'border-primary bg-accent' : 'border-border hover:bg-muted'}`}
          >
            <span className="block text-sm font-semibold">{o.label}</span>
            <span className="block text-xs text-text-secondary">{o.hint}</span>
          </button>
        ))}
      </div>
      {outcome === 'fixed' ? (
        <>
          <div className="grid gap-1.5">
            <Label htmlFor="sd-oc-note">What was done</Label>
            <Input id="sd-oc-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Re-ran the proctoring analysis for the affected candidates" />
          </div>
          <div className="grid gap-1.5">
            <Label>Cause</Label>
            <Select value={cause} onValueChange={setCause}>
              <SelectTrigger><SelectValue placeholder="Pick the cause" /></SelectTrigger>
              <SelectContent>{(meta.data?.rootCauses ?? []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="sd-oc-gh">GitHub issue <span className="font-normal text-text-muted">(optional)</span></Label>
            <Input id="sd-oc-gh" value={github} onChange={(e) => setGithub(e.target.value)} placeholder="https://github.com/TapTalent-ai/…/issues/123" />
            {!issueOk ? <p className="text-tiny text-danger">Paste the issue link (…/issues/123), not a pull request.</p> : null}
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="sd-oc-ev">How it was verified <span className="font-normal text-text-muted">(optional)</span></Label>
            <Textarea id="sd-oc-ev" rows={2} value={evidence} onChange={(e) => setEvidence(e.target.value)} />
          </div>
        </>
      ) : outcome === 'client_side' ? (
        <>
          <div className="grid gap-1.5">
            <Label htmlFor="sd-oc-finding">What you found</Label>
            <Textarea id="sd-oc-finding" rows={2} value={finding} onChange={(e) => setFinding(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="sd-oc-action">What the client needs to do</Label>
            <Textarea id="sd-oc-action" rows={2} value={clientAction} onChange={(e) => setClientAction(e.target.value)} />
          </div>
        </>
      ) : (
        <div className="grid gap-1.5">
          <Label htmlFor="sd-oc-why">Why no fix was needed</Label>
          <Textarea id="sd-oc-why" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. The customer withdrew the request; it works as designed" />
        </div>
      )}
    </Shell>
  )
}

export function ClientSideDialog({ c, open, onOpenChange }: { c: SupportCase; open: boolean; onOpenChange: (v: boolean) => void }) {
  const run = useDeskAction()
  const [finding, setFinding] = useState('')
  const [clientAction, setClientAction] = useState('')
  return (
    <Shell
      open={open}
      onOpenChange={onOpenChange}
      title="Cause is on the client's side"
      description="Use this when the problem is in the client's own setup (their network, devices, browser or IT policy), not in our product. Your part ends here: restore and fix count as met now, and helpdesk follows up with the client and closes it. No GitHub issue or test evidence is needed."
      busy={run.isPending}
      disabled={!finding.trim() || !clientAction.trim()}
      submit={{
        label: 'Hand back to helpdesk',
        onClick: () => run.mutate({ id: c.id, action: 'client-side', body: { finding, clientAction } }, { onSuccess: () => onOpenChange(false) }),
      }}
    >
      <div className="grid gap-1.5">
        <Label htmlFor="sd-cs-finding">What you found (may be shown to the customer)</Label>
        <Textarea id="sd-cs-finding" rows={3} value={finding} onChange={(e) => setFinding(e.target.value)} placeholder="e.g. The candidate's browser blocks microphone access under their company's IT policy" />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="sd-cs-action">What the client needs to do</Label>
        <Textarea id="sd-cs-action" rows={3} value={clientAction} onChange={(e) => setClientAction(e.target.value)} placeholder="e.g. Their IT allows microphone access for interview.taptalent.ai, then the candidate retries" />
      </div>
      <p className="text-tiny text-text-muted">Recorded with the cause "{CLIENT_SIDE_CAUSE}". If the client comes back and it is ours after all, Reopen sends it back to the team on the same clock history.</p>
    </Shell>
  )
}

export function FixedDialog({ c, open, onOpenChange, helpdesk }: { c: SupportCase; open: boolean; onOpenChange: (v: boolean) => void; helpdesk: boolean }) {
  const meta = useDeskMeta()
  const run = useDeskAction()
  const [note, setNote] = useState('')
  const [cause, setCause] = useState('')
  const [github, setGithub] = useState(c.githubUrl ?? '')
  const [evidence, setEvidence] = useState('')
  const [summary, setSummary] = useState<ResolutionSummary>({ problem: '', fix: '', cause: '', prevention: '' })
  const [drafting, setDrafting] = useState(false)
  const draft = () => {
    setDrafting(true)
    draftResolution(c.id)
      .then((d) => setSummary({ problem: d.problem ?? '', fix: d.fix ?? '', cause: d.cause ?? '', prevention: d.prevention ?? '' }))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setDrafting(false))
  }
  const hasSummary = Object.values(summary).some((v) => v.trim())
  // Engineering work needs its test evidence; the GitHub issue is optional (the cause may turn out to be on the client's side),
  // but anything pasted must be an issue, not a pull request. A request ends in a decision instead.
  const engineering = !helpdesk && Boolean(c.ownerTeam) && c.severity !== 'REQ'
  // A client-side cause has no change of ours to test; how the client confirmed it is enough, and optional.
  const clientCause = cause === CLIENT_SIDE_CAUSE
  const issueOk = GITHUB_ISSUE.test(github.trim())
  return (
    <Shell
      open={open}
      onOpenChange={onOpenChange}
      title={helpdesk ? 'Resolve' : c.severity === 'REQ' ? 'Decision made' : 'Mark fixed'}
      description="Helpdesk then confirms with the customer. If they do not reply, helpdesk is reminded at 24 and 48 hours and asked to review and close at 72. Nothing closes by itself."
      busy={run.isPending}
      disabled={!note.trim() || !cause || (engineering && !clientCause && !evidence.trim()) || (Boolean(github.trim()) && !issueOk)}
      submit={{
        label: helpdesk ? 'Resolve' : c.severity === 'REQ' ? 'Record decision' : 'Mark fixed',
        onClick: () =>
          run.mutate(
            { id: c.id, action: 'fixed', body: { note, rootCause: cause, githubUrl: github.trim() || null, evidence: evidence.trim() || null, summary: hasSummary ? summary : null } },
            { onSuccess: () => onOpenChange(false) },
          ),
      }}
    >
      <div className="grid gap-1.5">
        <Label htmlFor="sd-fix-note">What was done (one line, may be shown to the customer)</Label>
        <Input id="sd-fix-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Re-ran the AIVI scoring for the two candidates" />
      </div>
      <div className="grid gap-1.5">
        <Label>Cause</Label>
        <Select value={cause} onValueChange={setCause}>
          <SelectTrigger><SelectValue placeholder="Pick the cause" /></SelectTrigger>
          <SelectContent>{(meta.data?.rootCauses ?? []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {!helpdesk ? (
        <div className="grid gap-1.5">
          <Label htmlFor="sd-fix-gh">GitHub issue <span className="font-normal text-text-muted">(optional: leave it blank when the cause was outside our code)</span></Label>
          <Input id="sd-fix-gh" value={github} onChange={(e) => setGithub(e.target.value)} placeholder="https://github.com/TapTalent-ai/…/issues/123" />
          {github.trim() && !issueOk ? <p className="text-tiny text-danger">Paste the issue link (…/issues/123), not a pull request.</p> : null}
        </div>
      ) : null}
      {engineering ? (
        <div className="grid gap-1.5">
          <Label htmlFor="sd-fix-ev">Test evidence{clientCause ? <span className="font-normal text-text-muted"> (optional for a client-side cause)</span> : null}</Label>
          <Textarea id="sd-fix-ev" rows={3} value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder="How it was verified, in which environment, and a link to the test run or screenshots" />
        </div>
      ) : null}
      <div className="grid gap-2 rounded-lg border border-border-soft p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold">Resolution summary <span className="font-normal text-text-muted">(kept on the ticket)</span></p>
          <Button type="button" size="sm" variant="outline" disabled={drafting} onClick={draft}>
            <Sparkles className="size-4" /> {drafting ? 'Drafting…' : hasSummary ? 'Draft again' : 'Draft with AI'}
          </Button>
        </div>
        <p className="text-tiny text-text-muted">Drafted from the ticket and its activity. Check and edit it before saving: what you save is what the next person reads.</p>
        {(
          [
            ['problem', 'What was wrong', 'As the customer experienced it'],
            ['fix', 'What was done', 'The fix or the workaround'],
            ['cause', 'Cause', 'The underlying cause, or "Not yet known"'],
            ['prevention', 'Prevention', 'What stops it happening again, or "None needed"'],
          ] as const
        ).map(([k, label, hint]) => (
          <div key={k} className="grid gap-1">
            <Label htmlFor={`sd-sum-${k}`} className="text-xs">{label}</Label>
            <Textarea id={`sd-sum-${k}`} rows={2} value={summary[k]} placeholder={hint} onChange={(e) => setSummary((s) => ({ ...s, [k]: e.target.value }))} />
          </div>
        ))}
      </div>
    </Shell>
  )
}

export function ReassignDialog({ c, open, onOpenChange }: { c: SupportCase; open: boolean; onOpenChange: (v: boolean) => void }) {
  const meta = useDeskMeta()
  const run = useDeskAction()
  const [owner, setOwner] = useState(c.ownerTeam ?? HELPDESK)
  const [picked, setPicked] = useState<string | null>(null)
  const conv = useConversation(c.sourceId, c.sourceKind !== 'manual')
  const [reason, setReason] = useState('')
  const [edited, setHandoff] = useState<HandoffDraft | null>(null)
  const handoff = edited ?? prefillHandoff(c, conv.data)
  const team = owner === HELPDESK ? null : owner
  const people = team ? (meta.data?.teams.find((t) => t.name === team)?.members ?? []) : []
  const assignee = picked ?? (team ? (people.find((p) => p.isLead)?.id ?? NONE) : NONE)
  const setAssignee = (v: string) => setPicked(v)
  // The first time a ticket goes to engineering it carries the full handoff, however it gets there.
  const needsHandoff = Boolean(team) && !c.routedAt
  return (
    <Shell
      open={open}
      onOpenChange={onOpenChange}
      title="Reassign"
      description="The clock keeps running; the new owner gets the full history."
      busy={run.isPending}
      disabled={!reason.trim() || (Boolean(team) && assignee === NONE) || (needsHandoff && !handoffReady(handoff))}
      submit={{
        label: 'Reassign',
        onClick: () =>
          run.mutate(
            { id: c.id, action: 'reassign', body: { team, assigneeId: assignee === NONE ? null : assignee, reason, ...(needsHandoff ? handoffBody(handoff) : {}) } },
            { onSuccess: () => onOpenChange(false) },
          ),
      }}
    >
      <div className="grid gap-1.5">
        <Label>Team</Label>
        <Select value={owner} onValueChange={(v) => { setOwner(v); setPicked(null) }}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={HELPDESK}>Helpdesk</SelectItem>
            {(meta.data?.teams ?? []).map((t) => <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {team ? (
        <div className="grid gap-1.5">
          <Label>Person</Label>
          <Select value={assignee} onValueChange={setAssignee}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE} disabled>Pick who works on it</SelectItem>
              {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.displayName}{p.isLead ? ' (lead)' : ''}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      ) : null}
      <div className="grid gap-1.5">
        <Label htmlFor="sd-reassign">Why</Label>
        <Input id="sd-reassign" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. This is a NOVA scoring issue, not TOVI" />
      </div>
      {needsHandoff ? <HandoffFields value={handoff} onChange={setHandoff} /> : null}
    </Shell>
  )
}

const toLocalInput = (ms: number) => {
  const d = new Date(ms)
  d.setSeconds(0, 0)
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

/**
 * Escalate: asks for the CEO's, the team lead's and the Product & Solutions lead's attention now,
 * at any point (not only after a breach), with a reason and a recovery ETA. The ETA becomes the
 * enforcement point; it does not erase a breach.
 */
export function EscalateDialog({ c, open, onOpenChange }: { c: SupportCase; open: boolean; onOpenChange: (v: boolean) => void }) {
  const run = useDeskAction()
  const now = useNow()
  const [reason, setReason] = useState('')
  const [eta, setEta] = useState('')
  const etaMs = eta ? new Date(eta).getTime() : NaN
  const etaOk = Number.isFinite(etaMs) && etaMs > now
  return (
    <Shell
      open={open}
      onOpenChange={onOpenChange}
      title="Escalate"
      description="Alerts the CEO, the team lead and the Product & Solutions lead now. Record why, what decision or help is needed, and a realistic recovery ETA."
      busy={run.isPending}
      disabled={!reason.trim() || !etaOk}
      submit={{ label: 'Escalate', onClick: () => run.mutate({ id: c.id, action: 'escalate', body: { reason: reason.trim(), eta: new Date(etaMs).toISOString() } }, { onSuccess: () => onOpenChange(false) }) }}
    >
      <div className="grid gap-1.5">
        <Label htmlFor="sd-esc-reason">Why, and what is needed</Label>
        <Textarea id="sd-esc-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. The fix needs a data change on the client's account; need approval to run it today" />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="sd-esc-eta">Recovery ETA (your local time)</Label>
        <Input id="sd-esc-eta" type="datetime-local" value={eta} min={toLocalInput(now)} onChange={(e) => setEta(e.target.value)} />
        {eta && !etaOk ? <p className="text-tiny text-danger">The ETA must be in the future.</p> : null}
      </div>
    </Shell>
  )
}


export function DismissDialog({ c, open, onOpenChange }: { c: SupportCase; open: boolean; onOpenChange: (v: boolean) => void }) {
  const meta = useDeskMeta()
  const run = useDeskAction()
  const [reason, setReason] = useState('')
  return (
    <Shell
      open={open}
      onOpenChange={onOpenChange}
      title="Not a support issue"
      description="Removes it from the queue. It stays on record and can be reopened."
      busy={run.isPending}
      disabled={!reason}
      submit={{ label: 'Dismiss', onClick: () => run.mutate({ id: c.id, action: 'dismiss', body: { reason } }, { onSuccess: () => onOpenChange(false) }) }}
    >
      <Select value={reason} onValueChange={setReason}>
        <SelectTrigger><SelectValue placeholder="Pick a reason" /></SelectTrigger>
        <SelectContent>{(meta.data?.dismissReasons ?? []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
      </Select>
    </Shell>
  )
}

const localNow = () => {
  const d = new Date()
  d.setSeconds(0, 0)
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

/** A ticket raised outside Intercom (Viber, WhatsApp, Gmail): the clock starts when the customer first reported it. */
export function LogTicketDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (v: boolean) => void; onCreated: (c: SupportCase) => void }) {
  const clients = useClients()
  const run = useLogTicket()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [channel, setChannel] = useState('viber')
  const [clientId, setClientId] = useState(NONE)
  const [requester, setRequester] = useState('')
  const [reportedAt, setReportedAt] = useState(localNow)
  const [severity, setSeverity] = useState<Severity | ''>('')
  const now = useNow()
  const reported = reportedAt ? new Date(reportedAt) : null
  const valid = Boolean(title.trim()) && Boolean(requester.trim()) && reported !== null && !Number.isNaN(reported.getTime()) && reported.getTime() <= now + 60_000
  return (
    <Shell
      open={open}
      onOpenChange={onOpenChange}
      title="Log a ticket"
      description="For issues raised on Viber, WhatsApp or email that are not in Intercom. Enter when the customer first reported it: that is when the clock starts."
      busy={run.isPending}
      disabled={!valid}
      submit={{
        label: 'Log ticket',
        onClick: () =>
          run.mutate(
            { title, description, channel, clientId: clientId === NONE ? null : clientId, requester: requester.trim(), firstReportedAt: reported!.toISOString(), severity: severity || null },
            { onSuccess: (created) => { onOpenChange(false); onCreated(created) } },
          ),
      }}
    >
      <div className="grid gap-1.5">
        <Label htmlFor="sd-log-title">What is wrong</Label>
        <Input id="sd-log-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Candidates cannot open the TOVI interview link" />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="sd-log-desc">Details (optional)</Label>
        <Textarea id="sd-log-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="grid gap-1.5">
          <Label>Channel</Label>
          <Select value={channel} onValueChange={setChannel}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="viber">Viber</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
              <SelectItem value="gmail">Email</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="sd-log-at">Customer first reported at</Label>
          <Input id="sd-log-at" type="datetime-local" value={reportedAt} onChange={(e) => setReportedAt(e.target.value)} />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="grid gap-1.5">
          <Label>Client</Label>
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Not sure yet</SelectItem>
              {clients.map((cl) => <SelectItem key={cl.id} value={cl.id}>{cl.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="sd-log-who">Who reported it</Label>
          <Input id="sd-log-who" value={requester} onChange={(e) => setRequester(e.target.value)} placeholder="Name, and company or email" />
        </div>
      </div>
      <SeverityPicker value={severity} onChange={setSeverity} />
      <p className="text-tiny text-text-muted">Leave priority empty to triage it later; until then it runs on the P2 clock.</p>
    </Shell>
  )
}

/** Link another ticket: a duplicate, a related report, or part of the same incident. */
export function RelatedDialog({ c, open, onOpenChange }: { c: SupportCase; open: boolean; onOpenChange: (v: boolean) => void }) {
  const run = useDeskAction()
  const [kind, setKind] = useState<RelationKind>('related')
  const [q, setQ] = useState('')
  const [picked, setPicked] = useState<string | null>(null)
  const term = q.trim()
  const openCases = useDeskCases('all', term ? { q: term } : {})
  const closedCases = useDeskCases('closed', term ? { q: term } : {})
  const already = new Set((c.related ?? []).map((r) => r.caseId))
  const options = [...(openCases.data?.items ?? []), ...(closedCases.data?.items ?? [])].filter((o) => o.id !== c.id && !already.has(o.id)).slice(0, 30)
  return (
    <Shell
      open={open}
      onOpenChange={onOpenChange}
      title="Link a ticket"
      description="Both tickets show the link. Nothing changes on either clock."
      busy={run.isPending}
      disabled={!picked}
      submit={{ label: 'Link', onClick: () => picked && run.mutate({ id: c.id, action: 'related', body: { otherId: picked, kind } }, { onSuccess: () => onOpenChange(false) }) }}
    >
      <div className="grid gap-1.5">
        <Label>How they relate</Label>
        <Select value={kind} onValueChange={(v) => setKind(v as RelationKind)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{RELATION_CHOICES.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
        <p className="text-tiny text-text-muted">{RELATION_CHOICES.find((r) => r.id === kind)?.hint}</p>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="sd-rel-q">Find the ticket</Label>
        <Input id="sd-rel-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Reference, title, client or requester" />
        <ul className="grid max-h-64 gap-1 overflow-y-auto" role="listbox" aria-label="Tickets">
          {options.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                role="option"
                aria-selected={picked === o.id}
                onClick={() => setPicked(o.id)}
                className={`w-full rounded-md border px-3 py-2 text-left text-sm ${picked === o.id ? 'border-brand bg-brand-softer' : 'border-border-soft hover:border-brand/40'}`}
              >
                <span className="num mr-2 text-tiny text-text-muted">{o.reference}</span>
                {o.title}
                <span className="block text-tiny text-text-muted">{o.clientName ?? 'Client not matched'} · {STATE_LABEL[o.state]}</span>
              </button>
            </li>
          ))}
          {!options.length ? <li className="text-sm text-text-muted">{openCases.isPending ? 'Searching…' : 'No other tickets match.'}</li> : null}
        </ul>
      </div>
    </Shell>
  )
}
